// Default member code used for testing (same as wlas_1_demo page)
// TODO: auto-populate from broker office profile when API is available
export const WLAS_DEFAULT_MEMBER_CODE = 'wlas';

export type WlasParamType = 'text' | 'bool' | 'select' | 'color';

export interface WlasSelectOption {
  value: string;
  label: string;
}

export interface WlasParamDef {
  id: string;
  attr: string;
  type: WlasParamType;
  defaultVal: string | boolean;
  skipEmpty?: boolean;
  alwaysInclude?: boolean;
  /** Read-only params are shown in the snippet but not editable in the form */
  readonly?: boolean;
  options?: WlasSelectOption[];
  descriptionKey: string;
  docsAnchor?: string;
  section: WlasParamSection;
}

export type WlasParamSection = 'config' | 'display' | 'pagination' | 'theme';

export const WLAS_PARAMS: WlasParamDef[] = [
  // ── Config ──────────────────────────────────────────────────────────────
  {
    id: 'member-unique-code',
    attr: 'member-unique-code',
    type: 'text',
    defaultVal: WLAS_DEFAULT_MEMBER_CODE,
    alwaysInclude: true,
    readonly: true,
    section: 'config',
    descriptionKey: 'params.memberUniqueCode',
    docsAnchor: 'member-unique-code',
  },

  // ── Display ──────────────────────────────────────────────────────────────
  {
    id: 'show-nav',
    attr: 'show-nav',
    type: 'bool',
    defaultVal: true,
    section: 'display',
    descriptionKey: 'params.showNav',
    docsAnchor: 'show-nav',
  },
  {
    id: 'show-breadcrumb',
    attr: 'show-breadcrumb',
    type: 'bool',
    defaultVal: true,
    section: 'display',
    descriptionKey: 'params.showBreadcrumb',
    docsAnchor: 'show-breadcrumb',
  },
  {
    id: 'show-broker-info',
    attr: 'show-broker-info',
    type: 'bool',
    defaultVal: true,
    section: 'display',
    descriptionKey: 'params.showBrokerInfo',
    docsAnchor: 'show-broker-info',
  },
  {
    id: 'detail-as-modal',
    attr: 'detail-as-modal',
    type: 'bool',
    defaultVal: false,
    section: 'display',
    descriptionKey: 'params.detailAsModal',
    docsAnchor: 'detail-as-modal',
  },
  {
    id: 'hide-login-button',
    attr: 'hide-login-button',
    type: 'bool',
    defaultVal: false,
    section: 'display',
    descriptionKey: 'params.hideLoginButton',
    docsAnchor: 'hide-login-button',
  },
  {
    id: 'hide-toolbar',
    attr: 'hide-toolbar',
    type: 'bool',
    defaultVal: false,
    section: 'display',
    descriptionKey: 'params.hideToolbar',
    docsAnchor: 'hide-toolbar',
  },
  {
    id: 'transparent-bg',
    attr: 'transparent-bg',
    type: 'bool',
    defaultVal: false,
    section: 'display',
    descriptionKey: 'params.transparentBg',
    docsAnchor: 'transparent-bg',
  },
  {
    id: 'collapse-on-empty',
    attr: 'collapse-on-empty',
    type: 'bool',
    defaultVal: false,
    section: 'display',
    descriptionKey: 'params.collapseOnEmpty',
    docsAnchor: 'collapse-on-empty',
  },
  {
    id: 'mini-slider',
    attr: 'mini-slider',
    type: 'bool',
    defaultVal: false,
    section: 'display',
    descriptionKey: 'params.miniSlider',
    docsAnchor: 'mini-slider',
  },
  {
    id: 'initial-tabs',
    attr: 'initial-tabs',
    type: 'select',
    defaultVal: '',
    skipEmpty: true,
    section: 'display',
    descriptionKey: 'params.initialTabs',
    docsAnchor: 'initial-tabs',
    options: [
      { value: '', label: 'selectOptions.initialTabs.default' },
      { value: 'TILE', label: 'selectOptions.initialTabs.tile' },
      { value: 'LIST', label: 'selectOptions.initialTabs.list' },
    ],
  },
  {
    id: 'exclude-office-id',
    attr: 'exclude-office-id',
    type: 'text',
    defaultVal: '',
    skipEmpty: true,
    section: 'display',
    descriptionKey: 'params.excludeOfficeId',
    docsAnchor: 'exclude-office-id',
  },

  // ── Pagination ────────────────────────────────────────────────────────────
  {
    id: 'show-page-size',
    attr: 'show-page-size',
    type: 'bool',
    defaultVal: true,
    section: 'pagination',
    descriptionKey: 'params.showPageSize',
    docsAnchor: 'show-page-size',
  },
  {
    id: 'hide-pagination',
    attr: 'hide-pagination',
    type: 'bool',
    defaultVal: false,
    section: 'pagination',
    descriptionKey: 'params.hidePagination',
    docsAnchor: 'hide-pagination',
  },
  {
    id: 'default-page-size',
    attr: 'default-page-size',
    type: 'select',
    defaultVal: '12',
    section: 'pagination',
    descriptionKey: 'params.defaultPageSize',
    docsAnchor: 'default-page-size',
    options: [
      { value: '3', label: '3' },
      { value: '6', label: '6' },
      { value: '12', label: '12' },
      { value: '24', label: '24' },
      { value: '48', label: '48' },
    ],
  },

  // ── Theme ─────────────────────────────────────────────────────────────────
  {
    id: 'theme-primary',
    attr: 'theme-primary',
    type: 'color',
    defaultVal: '#dc1c2e',
    skipEmpty: true,
    section: 'theme',
    descriptionKey: 'params.themePrimary',
    docsAnchor: 'theme-primary',
  },
  {
    id: 'theme-btn-text',
    attr: 'theme-btn-text',
    type: 'color',
    defaultVal: '#ffffff',
    skipEmpty: true,
    section: 'theme',
    descriptionKey: 'params.themeBtnText',
    docsAnchor: 'theme-btn-text',
  },
  {
    id: 'theme-highlight',
    attr: 'theme-highlight',
    type: 'color',
    defaultVal: '#dc1c2e',
    skipEmpty: true,
    section: 'theme',
    descriptionKey: 'params.themeHighlight',
    docsAnchor: 'theme-highlight',
  },
  {
    id: 'theme-link',
    attr: 'theme-link',
    type: 'color',
    defaultVal: '#dc1c2e',
    skipEmpty: true,
    section: 'theme',
    descriptionKey: 'params.themeLink',
    docsAnchor: 'theme-link',
  },
  {
    id: 'theme-font',
    attr: 'theme-font',
    type: 'select',
    defaultVal: 'Montserrat',
    alwaysInclude: true,
    section: 'theme',
    descriptionKey: 'params.themeFont',
    docsAnchor: 'theme-font',
    options: [
      { value: '', label: 'selectOptions.themeFont.default' },
      { value: 'Montserrat', label: 'Montserrat' },
      { value: 'Urbanist', label: 'Urbanist' },
    ],
  },
  {
    id: 'theme-radius',
    attr: 'theme-radius',
    type: 'select',
    defaultVal: '0px',
    section: 'theme',
    descriptionKey: 'params.themeRadius',
    docsAnchor: 'theme-radius',
    options: [
      { value: '0px', label: '0px' },
      { value: '4px', label: '4px' },
      { value: '6px', label: '6px' },
      { value: '8px', label: '8px' },
      { value: '12px', label: '12px' },
      { value: '20px', label: '20px' },
    ],
  },
];

export const WLAS_SECTIONS: { key: WlasParamSection; labelKey: string }[] = [
  { key: 'config', labelKey: 'sections.config' },
  { key: 'display', labelKey: 'sections.display' },
  { key: 'pagination', labelKey: 'sections.pagination' },
  { key: 'theme', labelKey: 'sections.theme' },
];
